/**
 * Tenant Usage Dashboard
 * Dashboard for Super Admin to view usage statistics for all tenants
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import axios from 'axios';
import {
  BarChart3,
  Download,
  RefreshCw,
  Filter,
  Search,
  AlertTriangle,
  CheckCircle,
  Package,
  Database,
  HardDrive,
  Zap,
  TrendingUp,
  X,
  Eye,
  Info,
  Trash2,
  CheckSquare
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const TenantUsageDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [packageFilter, setPackageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [entityMappings, setEntityMappings] = useState(null);

  useEffect(() => {
    loadEntityMappings();
    loadDashboardData();
  }, []);

  const loadEntityMappings = async () => {
    try {
      const response = await axios.get('/api/usage-control/entity-mappings');
      if (response.data.success) {
        setEntityMappings(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load entity mappings:', error);
      // Fallback to empty mappings if API fails
      setEntityMappings({ pluralToSingular: {}, entityNameToPlural: {}, tableToEntityName: {}, allEntities: [] });
    }
  };

  useEffect(() => {
    applyFilters();
  }, [dashboardData, searchTerm, packageFilter, statusFilter]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/usage-control/dashboard/all-tenants');
      if (response.data.success) {
        setDashboardData(response.data.data);
        setFilteredData(response.data.data.tenants || []);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
    toast.success('Dashboard data refreshed');
  };

  const applyFilters = () => {
    if (!dashboardData || !dashboardData.tenants) return;

    let filtered = [...dashboardData.tenants];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(tenant =>
        tenant.tenantName.toLowerCase().includes(term) ||
        tenant.tenantCode.toLowerCase().includes(term) ||
        (tenant.package?.packageName || '').toLowerCase().includes(term)
      );
    }

    // Package filter
    if (packageFilter !== 'all') {
      if (packageFilter === 'no-package') {
        filtered = filtered.filter(tenant => !tenant.package);
      } else {
        filtered = filtered.filter(tenant => tenant.package?.packageId === parseInt(packageFilter));
      }
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'near-limit') {
        filtered = filtered.filter(tenant => {
          if (!tenant.usage) return false;
          return tenant.usage.storage.percentage >= 80 ||
                 tenant.usage.aiTokens.percentage >= 80 ||
                 Object.values(tenant.usage.entities.percentages || {}).some(p => p >= 80);
        });
      } else if (statusFilter === 'over-limit') {
        filtered = filtered.filter(tenant => {
          if (!tenant.usage) return false;
          return tenant.usage.storage.percentage >= 100 ||
                 tenant.usage.aiTokens.percentage >= 100 ||
                 Object.values(tenant.usage.entities.percentages || {}).some(p => p >= 100);
        });
      }
    }

    setFilteredData(filtered);
  };

  const handleExport = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error('No data to export');
      return;
    }

    const csv = [
      ['Tenant Code', 'Tenant Name', 'Package', 'Storage Used', 'Storage Limit', 'Storage %', 
       'AI Tokens (Monthly)', 'AI Tokens Limit', 'AI Tokens %', 'Total Entities', 'Alerts'].join(','),
      ...filteredData.map(tenant => [
        tenant.tenantCode,
        tenant.tenantName,
        tenant.package?.packageName || 'No Package',
        formatBytes(tenant.usage?.storage?.total || 0),
        formatBytes(tenant.usage?.storage?.limit || 0),
        `${(tenant.usage?.storage?.percentage || 0).toFixed(2)}%`,
        (tenant.usage?.aiTokens?.monthly || 0).toLocaleString(),
        (tenant.usage?.aiTokens?.limit || 0).toLocaleString(),
        `${(tenant.usage?.aiTokens?.percentage || 0).toFixed(2)}%`,
        tenant.usage?.entities?.total || 0,
        tenant.alerts?.length || 0
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tenant-usage-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success('Data exported successfully');
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getPercentageColor = (percentage) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPackagesList = () => {
    if (!dashboardData || !dashboardData.tenants) return [];
    const packages = new Set();
    dashboardData.tenants.forEach(tenant => {
      if (tenant.package) {
        packages.add(JSON.stringify({ id: tenant.package.packageId, name: tenant.package.packageName }));
      }
    });
    return Array.from(packages).map(p => JSON.parse(p));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart3 className="w-8 h-8" />
            Tenant Usage Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Monitor resource usage across all tenants
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <Filter className="w-5 h-5" />
            Filters
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Download className="w-5 h-5" />
            Export
          </button>
        </div>
      </div>

      {dashboardData?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            title="Total Tenants"
            value={dashboardData.summary.totalTenants}
            icon={<Database className="w-6 h-6" />}
            color="blue"
          />
          <SummaryCard
            title="With Packages"
            value={dashboardData.summary.tenantsWithPackages}
            icon={<Package className="w-6 h-6" />}
            color="green"
          />
          <SummaryCard
            title="Near Limit"
            value={dashboardData.summary.tenantsNearLimit}
            icon={<AlertTriangle className="w-6 h-6" />}
            color="yellow"
          />
          <SummaryCard
            title="Over Limit"
            value={dashboardData.summary.tenantsOverLimit}
            icon={<TrendingUp className="w-6 h-6" />}
            color="red"
          />
        </div>
      )}

      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search tenants..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Package
              </label>
              <select
                value={packageFilter}
                onChange={(e) => setPackageFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Packages</option>
                <option value="no-package">No Package</option>
                {getPackagesList().map(pkg => (
                  <option key={pkg.id} value={pkg.id}>{pkg.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="all">All Status</option>
                <option value="near-limit">Near Limit (≥80%)</option>
                <option value="over-limit">Over Limit (≥100%)</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tenant
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Package
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Storage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  AI Tokens
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Entities
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Alerts
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredData.map((tenant) => (
                <TenantUsageRow
                  key={tenant.tenantId}
                  tenant={tenant}
                  formatBytes={formatBytes}
                  getPercentageColor={getPercentageColor}
                  onViewDetails={() => {
                    setSelectedTenant(tenant);
                    setShowDetailModal(true);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
        {filteredData.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">No tenants found</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedTenant && (
        <TenantDetailModal
          tenant={selectedTenant}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTenant(null);
          }}
          formatBytes={formatBytes}
          getPercentageColor={getPercentageColor}
          onRefresh={async () => {
            await handleRefresh();
            // Reload tenant data to get updated usage
            const response = await axios.get('/api/usage-control/dashboard/all-tenants');
            if (response.data.success) {
              const updatedTenant = response.data.data.tenants?.find(
                t => t.tenantCode === selectedTenant.tenantCode
              );
              if (updatedTenant) {
                setSelectedTenant(updatedTenant);
              }
            }
          }}
          entityMappings={entityMappings}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300',
    green: 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300',
    yellow: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300',
    red: 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400">{title}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
};

const TenantUsageRow = ({ tenant, formatBytes, getPercentageColor, onViewDetails }) => {
  const usage = tenant.usage || {};
  const storage = usage.storage || {};
  const aiTokens = usage.aiTokens || {};
  const entities = usage.entities || {};

  return (
    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
      <td className="px-6 py-4 whitespace-nowrap">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {tenant.tenantName}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {tenant.tenantCode}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {tenant.package ? (
          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded">
            {tenant.package.packageName}
          </span>
        ) : (
          <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded">
            No Package
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 dark:text-white">
          {formatBytes(storage.total || 0)} / {formatBytes(storage.limit || 0)}
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
          <div
            className={`h-2 rounded-full ${getPercentageColor(storage.percentage || 0)}`}
            style={{ width: `${Math.min(storage.percentage || 0, 100)}%` }}
          />
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {(storage.percentage || 0).toFixed(1)}%
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 dark:text-white">
          {(aiTokens.monthly || 0).toLocaleString()} / {(aiTokens.limit || 0).toLocaleString()}
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-1">
          <div
            className={`h-2 rounded-full ${getPercentageColor(aiTokens.percentage || 0)}`}
            style={{ width: `${Math.min(aiTokens.percentage || 0, 100)}%` }}
          />
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {(aiTokens.percentage || 0).toFixed(1)}%
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 dark:text-white">
          {(entities.total || 0).toLocaleString()}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {Object.keys(entities.byType || {}).length} types
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {tenant.alerts && tenant.alerts.length > 0 ? (
          <span className="flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 rounded">
            <AlertTriangle className="w-3 h-3" />
            {tenant.alerts.length}
          </span>
        ) : (
          <CheckCircle className="w-5 h-5 text-green-500" />
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <button
          onClick={onViewDetails}
          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          title="View Details"
        >
          <Eye className="w-4 h-4" />
          Details
        </button>
      </td>
    </tr>
  );
};

const TenantDetailModal = ({ tenant, onClose, formatBytes, getPercentageColor, onRefresh, entityMappings }) => {
  const [clearingAlerts, setClearingAlerts] = useState(false);
  const [resolvingAlerts, setResolvingAlerts] = useState(false);
  const [clearingNoMessage, setClearingNoMessage] = useState(false);
  const usage = tenant.usage || {};
  const storage = usage.storage || {};
  const aiTokens = usage.aiTokens || {};
  const entities = usage.entities || {};
  // Entity limits structure: { Customer: { limit: 1000, enforcement: 'soft' }, ... }
  const entityLimits = usage.entities?.limits || {};
  const entityPercentages = usage.entities?.percentages || {};
  const entityByType = usage.entities?.byType || {};
  
  // Debug: Log entity limits structure when modal opens
  React.useEffect(() => {
    if (tenant && tenant.usage) {
      console.log('🔍 Tenant Usage Debug:', {
        tenantCode: tenant.tenantCode,
        tenantName: tenant.tenantName,
        package: tenant.package,
        usageEntities: tenant.usage.entities,
        entityLimits: entityLimits,
        entityByType: entityByType,
        entityPercentages: entityPercentages,
        limitsKeys: Object.keys(entityLimits),
        byTypeKeys: Object.keys(entityByType),
        sampleLimit: entityLimits && Object.keys(entityLimits).length > 0 
          ? { key: Object.keys(entityLimits)[0], value: entityLimits[Object.keys(entityLimits)[0]] }
          : null,
        fullUsage: JSON.stringify(tenant.usage, null, 2)
      });
    }
  }, [tenant, entityLimits, entityByType, entityPercentages]);

  const getAlertSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 border-red-300 dark:border-red-700';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700';
      case 'info':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 border-blue-300 dark:border-blue-700';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const handleClearOldAlerts = async () => {
    if (!window.confirm('Are you sure you want to clear old alerts (older than 30 days)? This action cannot be undone.')) {
      return;
    }

    try {
      setClearingAlerts(true);
      const response = await axios.delete(
        `/api/usage-control/alerts/${tenant.tenantCode}/clear-old`,
        { params: { daysOld: 30, onlyResolved: true } }
      );
      
      if (response.data.success) {
        toast.success(`Cleared ${response.data.deletedCount} old alerts`);
        if (onRefresh) {
          await onRefresh();
        }
      }
    } catch (error) {
      console.error('Failed to clear old alerts:', error);
      toast.error(error.response?.data?.error || 'Failed to clear old alerts');
    } finally {
      setClearingAlerts(false);
    }
  };

  const handleResolveAllAlerts = async () => {
    if (!window.confirm('Are you sure you want to resolve all active alerts? This will mark all alerts as resolved.')) {
      return;
    }

    try {
      setResolvingAlerts(true);
      const response = await axios.post(
        `/api/usage-control/alerts/${tenant.tenantCode}/resolve-all`
      );
      
      if (response.data.success) {
        toast.success(`Resolved ${response.data.resolvedCount} alerts`);
        if (onRefresh) {
          await onRefresh();
        }
      }
    } catch (error) {
      console.error('Failed to resolve alerts:', error);
      toast.error(error.response?.data?.error || 'Failed to resolve alerts');
    } finally {
      setResolvingAlerts(false);
    }
  };

  const handleClearAllAlerts = async () => {
    if (!window.confirm(`Are you sure you want to delete ALL ${tenant.alerts?.length || 0} alerts for this tenant? This action cannot be undone.`)) {
      return;
    }

    try {
      setClearingNoMessage(true);
      const response = await axios.delete(
        `/api/usage-control/alerts/${tenant.tenantCode}/clear-all`
      );
      
      if (response.data.success) {
        toast.success(`Cleared ${response.data.deletedCount} alerts`);
        if (onRefresh) {
          await onRefresh();
        }
      }
    } catch (error) {
      console.error('Failed to clear all alerts:', error);
      toast.error(error.response?.data?.error || 'Failed to clear alerts');
    } finally {
      setClearingNoMessage(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {tenant.tenantName}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {tenant.tenantCode} • Last updated: {formatDate(tenant.timestamp)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Package Information */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <Package className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Package Information</h3>
            </div>
            {tenant.package ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Package:</span>
                  <span className="px-2 py-1 text-sm bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded">
                    {tenant.package.packageName} ({tenant.package.packageCode})
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">No package assigned</p>
            )}
          </div>

          {/* Storage Details */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <HardDrive className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Storage Usage</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Used</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatBytes(storage.total || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Limit</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {formatBytes(storage.limit || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Files</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {(storage.fileCount || 0).toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div
                  className={`h-3 rounded-full ${getPercentageColor(storage.percentage || 0)}`}
                  style={{ width: `${Math.min(storage.percentage || 0, 100)}%` }}
                />
              </div>
              <div className="text-center">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {(storage.percentage || 0).toFixed(2)}%
                </span>
              </div>
            </div>
          </div>

          {/* AI Tokens Details */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="w-5 h-5 text-yellow-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Token Usage</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Daily</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {(aiTokens.daily || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Monthly</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {(aiTokens.monthly || 0).toLocaleString()} / {(aiTokens.limit || 0).toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                <div
                  className={`h-3 rounded-full ${getPercentageColor(aiTokens.percentage || 0)}`}
                  style={{ width: `${Math.min(aiTokens.percentage || 0, 100)}%` }}
                />
              </div>
              <div className="text-center">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {(aiTokens.percentage || 0).toFixed(2)}%
                </span>
              </div>
            </div>
          </div>

          {/* Entity Usage Details */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Entity Usage</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Entities</span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {(entities.total || 0).toLocaleString()}
                </span>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {Object.keys(entityByType).length > 0 ? (
                  Object.entries(entityByType)
                    // Filter out duplicate entities (e.g., KnowledgeArticles if Knowledge already exists)
                    .filter(([entityType, count], index, arr) => {
                      // Fallback mapping for when entityMappings is not loaded yet
                      const fallbackMapping = {
                        'Customers': 'Customer',
                        'Leads': 'Lead',
                        'Opportunities': 'Opportunity',
                        'Activities': 'Activity',
                        'Notes': 'SalesNote',
                        'SupportTickets': 'SupportTicket',
                        'Knowledge': 'Knowledge',
                        'KnowledgeArticles': 'Knowledge',
                        'Users': 'User'
                      };
                      
                      // Use entity mappings from API, fallback to hardcoded mapping if not loaded
                      const pluralToSingular = entityMappings?.pluralToSingular || fallbackMapping;
                      const entityNameToPlural = entityMappings?.entityNameToPlural || {};
                      
                      // Get canonical name for this entity type
                      const canonicalName = pluralToSingular[entityType] || 
                                           pluralToSingular[entityType?.toLowerCase()] ||
                                           entityType;
                      
                      // Check if there's another entry with the same canonical name
                      const hasDuplicate = arr.some(([otherType, otherCount], otherIndex) => {
                        if (index === otherIndex) return false;
                        const otherCanonical = pluralToSingular[otherType] || 
                                              pluralToSingular[otherType?.toLowerCase()] ||
                                              otherType;
                        return otherCanonical === canonicalName;
                      });
                      
                      // Keep this entry if it's the first one with this canonical name, or if no duplicates found
                      if (hasDuplicate) {
                        // Prefer the one that matches the canonical name exactly
                        const pluralName = entityNameToPlural[canonicalName] || fallbackMapping[canonicalName + 's'] || canonicalName + 's';
                        return entityType === pluralName || entityType === canonicalName;
                      }
                      
                      return true;
                    })
                    .map(([entityType, count]) => {
                      // Fallback mapping for when entityMappings is not loaded yet
                      const fallbackMapping = {
                        'Customers': 'Customer',
                        'Leads': 'Lead',
                        'Opportunities': 'Opportunity',
                        'Activities': 'Activity',
                        'Notes': 'SalesNote',
                        'SupportTickets': 'SupportTicket',
                        'Knowledge': 'Knowledge',
                        'KnowledgeArticles': 'Knowledge',
                        'Users': 'User'
                      };
                      
                      // Use entity mappings from API, fallback to hardcoded mapping if not loaded
                      const pluralToSingular = entityMappings?.pluralToSingular || fallbackMapping;
                      
                      // Get limit config - try exact match first, then mapped name, then case variations
                      let limitConfig = entityLimits[entityType];
                      
                      // Try to get canonical name from plural name
                      const canonicalName = pluralToSingular[entityType] || 
                                           pluralToSingular[entityType?.toLowerCase()] ||
                                           entityType;
                      
                      if (!limitConfig && canonicalName !== entityType) {
                        // Try mapped singular name
                        limitConfig = entityLimits[canonicalName];
                      }
                      
                      if (!limitConfig) {
                        // Try case-insensitive match
                        const entityTypeLower = entityType.toLowerCase();
                        const entityTypeUpper = entityType.toUpperCase();
                        limitConfig = entityLimits[entityTypeLower] || entityLimits[entityTypeUpper] || 
                                     Object.entries(entityLimits).find(([key]) => 
                                       key.toLowerCase() === entityTypeLower
                                     )?.[1];
                      }
                      
                      // Extract limit value - handle both { limit: X } and direct value
                      const limit = limitConfig?.limit ?? limitConfig?.Limit ?? 
                                   (typeof limitConfig === 'number' ? limitConfig : 0);
                      const percentage = entityPercentages[entityType] || 0;
                      const enforcement = limitConfig?.enforcement || limitConfig?.Enforcement || 'soft';

                    return (
                      <div key={entityType} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {entityType}
                            </span>
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              enforcement === 'hard' 
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                            }`}>
                              {enforcement}
                            </span>
                          </div>
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            {count.toLocaleString()} / {limit.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${getPercentageColor(percentage)}`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                          {percentage.toFixed(2)}%
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                    No entity data available
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Alerts */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Alerts</h3>
                {tenant.alerts && tenant.alerts.length > 0 && (
                  <span className="px-2 py-1 text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300 rounded">
                    {tenant.alerts.length}
                  </span>
                )}
              </div>
              {tenant.alerts && tenant.alerts.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleResolveAllAlerts}
                    disabled={resolvingAlerts}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Resolve all active alerts"
                  >
                    <CheckSquare className="w-4 h-4" />
                    {resolvingAlerts ? 'Resolving...' : 'Resolve All'}
                  </button>
                  <button
                    onClick={handleClearAllAlerts}
                    disabled={clearingNoMessage}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Delete all alerts"
                  >
                    <Trash2 className="w-4 h-4" />
                    {clearingNoMessage ? 'Clearing...' : 'Clear All'}
                  </button>
                  <button
                    onClick={handleClearOldAlerts}
                    disabled={clearingAlerts}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Clear old alerts (older than 30 days)"
                  >
                    <Trash2 className="w-4 h-4" />
                    {clearingAlerts ? 'Clearing...' : 'Clear Old'}
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              {tenant.alerts && tenant.alerts.length > 0 ? (
                tenant.alerts.map((alert, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-3 ${getAlertSeverityColor(alert.severity)}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="text-sm font-semibold">{alert.type || 'Alert'}</span>
                          {alert.severity && (
                            <span className="text-xs uppercase">{alert.severity}</span>
                          )}
                        </div>
                        <p className="text-sm mt-1">{alert.message || alert.description || 'No message'}</p>
                        {alert.resourceType && (
                          <p className="text-xs mt-1 opacity-75">
                            Resource: {alert.resourceType}
                          </p>
                        )}
                      </div>
                      {alert.createdAt && (
                        <span className="text-xs opacity-75 ml-2">
                          {formatDate(alert.createdAt)}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 py-4">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>No active alerts</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantUsageDashboard;

